import {
    ReportPeriodWithLabel,
    ReportWithNestedGroupsRow,
    ReportWithNestedGroupsRowGroup,
    ReportWithNestedGroupsRowLine,
} from "@common/types/reports/types";
import { BalanceSheetReport } from "./types";
import { BalanceSheetViewRow } from "./balanceSheetViewTypes";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// The intercompany due-to line reuses its account's code with a `:due-to`
// presentation suffix so it stays a distinct row from the due-from line
// (balance-sheet-report-builder.service.ts). That suffix is not a real account
// code, so strip it before the value flows into GL/elimination drill-down URLs.
const DUE_TO_KEY_SUFFIX = ":due-to";

function toAccountCode(key: string): string {
    return key.endsWith(DUE_TO_KEY_SUFFIX)
        ? key.slice(0, -DUE_TO_KEY_SUFFIX.length)
        : key;
}

// A keyless row has no code to filter the General Ledger by, so it renders its
// amounts without a drill-down link instead of falling back to another identifier.
function toAccountCodes(row: ReportWithNestedGroupsRowLine): string[] {
    const accountCode = toAccountCode(row.key);
    return accountCode ? [accountCode] : [];
}

function getNestedAccountCodes(row: ReportWithNestedGroupsRow): string[] {
    if ("rows" in row) {
        return row.rows.flatMap(getNestedAccountCodes);
    }
    return toAccountCodes(row);
}

let rowIdCounter = 0;
function nextId(prefix: string): string {
    return `${prefix}:${rowIdCounter++}`;
}

// ---------------------------------------------------------------------------
// Core recursive builder
// ---------------------------------------------------------------------------

function buildGroupRows(
    group: ReportWithNestedGroupsRowGroup,
    periods: ReportPeriodWithLabel[],
    depth: number,
): BalanceSheetViewRow[] {
    const accountCodes = getNestedAccountCodes(group);
    const totals = group.totals ?? [];

    const childRows: BalanceSheetViewRow[] = [];

    for (const row of group.rows) {
        if ("rows" in row) {
            // Nested group — recurse
            const nestedHeader: BalanceSheetViewRow = {
                id: nextId(`group:${row.label}`),
                label: row.label,
                values: row.totals ?? [],
                isTotal: false,
                isExpandable: true,
                accountCodes: getNestedAccountCodes(row),
                periods,
                children: [...buildGroupRows(row, periods, depth + 1)],
            };
            childRows.push(nestedHeader);
        } else {
            // Leaf line
            childRows.push({
                id: nextId(`line:${row.label}`),
                label: row.label,
                values: row.values,
                isTotal: false,
                isExpandable: false,
                accountCodes: toAccountCodes(row),
                periods,
            });
        }
    }

    // Total row for this group
    childRows.push({
        id: nextId(`total:${group.label}`),
        label: `Total ${group.label}`,
        values: totals,
        isTotal: true,
        isExpandable: false,
        accountCodes,
        periods,
    });

    return childRows;
}

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

/**
 * Converts a BalanceSheetReport into a nested row model for ViewTable.
 * Top-level groups (Assets, Liabilities, Equity…) become expandable header
 * rows; their children are leaf lines + a "Total <Group>" row.
 */
export function buildBalanceSheetRows(
    report: BalanceSheetReport,
): BalanceSheetViewRow[] {
    rowIdCounter = 0;
    return report.rows.map((group) => {
        const accountCodes = getNestedAccountCodes(group);
        const children = buildGroupRows(group, report.periods, 0);

        return {
            id: nextId(`section:${group.label}`),
            label: group.label,
            values: group.totals ?? [],
            isTotal: false,
            isExpandable: true,
            accountCodes,
            periods: report.periods,
            children,
        };
    });
}
